<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      endpoint="reports/customer/birthdays"
      @onDataLoaded="onDataLoaded"
      :settings="{ theme: 'brithday ag-theme-quartz', dateRangeFormat: 'DD-MM' }"
    >
    </ReportsTable>
    <el-dialog v-model="cashierPinDialogOpen" @close="dialogClosed" width="30%">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('dialogs.enterCashierPin') }}</p>
        </div>

        <!-- password input up to 6 chars -->
        <div class="my-2">
          <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            ref="cashierPinInput"
            @keyup.enter="cashierPinAction()"
          />
        </div>

        <!-- finish payment -->
        <div class="flex items-center justify-between mb-2">
          <horizontal-button
            class="text-lg complete-payment"
            :title="t('dialogs.confirm')"
            @click="cashierPinAction()"
            :rounded="false"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute, useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatAge, formatPostgresDate } from '@/utils/date'
import User from '@/models/user/User'
import BirthdayMessageCellRenderer from '@/views/reports/v2/customers/BirthdayMessageCellRenderer.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import { ElMessage } from 'element-plus'
import {useSettingsStore} from "@/stores/settingsStore";

// i18n
const { t } = useI18n({
  messages: {
    en: {
      customerName: 'Customer Name',
      dateOfBirth: 'Date Of Birth',
      age: 'Age',
      phone1: 'Phone 1',
      phone2: 'Phone 2',
      messageSent: 'Message Sent?',
      senderName: 'Sender Name',
      updated: 'Updated'
    },
    ar: {
      customerName: 'اسم الزبون',
      dateOfBirth: 'تاريخ الميلاد',
      age: 'العمر',
      phone1: 'رقم الهاتف',
      phone2: 'رقم الهاتف 2',
      messageSent: 'تم ارسال الرسالة؟',
      senderName: 'اسم المرسل',
      updated: 'تم التحديث'
    }
  }
})

const cashierPin = ref('')
const cashierPinDialogOpen = ref(false)
const cashierPinAction: any = ref()
const cashierPinInput: any = ref()

const tempData: any = ref()
const tempValue: any = ref()
const tempUpdateCheck: any = ref()
const tempAllOk: any = ref(false)


// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

onBeforeMount(async () => {
  columnDefs.value = [
    {
      headerName: t('customerName'),
      checkboxSelection: true,
      headerCheckboxSelection: true,
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${data.name}`
      }
    },
    {
      headerName: t('dateOfBirth'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatPostgresDate(data.dateOfBirth, 'DD/MM/YYYY')
      }
    },
    {
      headerName: t('age'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatAge(String(data.dateOfBirth))
      }
    },
    {
      headerName: t('phone1'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${data.phone}`
      }
    },
    {
      headerName: t('phone2'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${data.phone2}`
      }
    },
    {
      headerName: t('messageSent'),
      cellRenderer: BirthdayMessageCellRenderer,
      cellRendererParams: {
        handleCheckChange: handleCheckChange
      }
    },
    {
      headerName: t('senderName'),
      field: 'sentBy'
    }
  ]
})

function onDataLoaded(data: any) {}

async function createBirthdayMessage() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    if (cashierPin.value === '') {
      cashierPinDialogOpen.value = false
      ElMessage({
        message: t('dialogs.errCashierPinRequired'),
        type: 'error'
      })
      return
    }
  }

  try {
    const resp = await http.post<any[]>(
      '/customer/birthday-messages',
      {
        customerID: tempData.value.id
      },
      {
        headers: { pin: cashierPin.value }
      }
    )
    ElMessage({
      message: t('updated'),
      type: 'success'
    })
    tempAllOk.value = true;
  } catch (error) {
    console.log(error);
    ElMessage({
      message: error.title,
      type: 'error'
    })
    tempUpdateCheck.value(false);
  }
  rtable.value.navigate(rtable.value.payload)
  cashierPinDialogOpen.value = false
  cashierPin.value = ''
}

async function removeBirthdayMessage() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    if (cashierPin.value === '') {
      cashierPinDialogOpen.value = false
      ElMessage({
        message: t('dialogs.errCashierPinRequired'),
        type: 'error'
      })
      return
    }
  }


  try {
    const resp = await http.delete<any[]>(
      '/customer/birthday-messages/' + tempData.value.bmID,
      {
        headers: { pin: cashierPin.value }
      }
    )
    ElMessage({
      message: t('updated'),
      type: 'success'
    })
    tempAllOk.value = true;
  } catch (error) {
    ElMessage({
      message: error.title,
      type: 'error'
    })
    tempUpdateCheck.value(true);
  }
  rtable.value.navigate(rtable.value.payload)
  cashierPinDialogOpen.value = false
  cashierPin.value = ''
}

function handleCheckChange(event: any, data: any, updateCheck: any) {
  cashierPinAction.value = event ? createBirthdayMessage : removeBirthdayMessage
  tempData.value = data;
  tempValue.value = !event;
  tempUpdateCheck.value = updateCheck;

  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    cashierPinDialogOpen.value = true

    nextTick(() => {
      setTimeout(() => {
        /* @ts-ignore */
        cashierPinInput.value.focus()
      }, 100)
    })
    return;
  }
  cashierPinAction.value();
}

function dialogClosed() {
  if (tempAllOk.value) {
    tempAllOk.value = false;
    return;
  }

  tempUpdateCheck.value(tempValue.value)
  tempUpdateCheck.value = null;
  tempData.value = null;
}
</script>
